import { useState, useMemo, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import {
  Search, Sparkles, Zap, Star,
  Trophy, BarChart3, Clock, Users, BookOpen, LayoutGrid,
  Eye, Heart, ArrowRight, ExternalLink, ChevronRight,
  TrendingUp, Table as TableIcon, Download, Loader2,
  Database, RefreshCw, Calendar, Shield,
} from 'lucide-react'
import { SEARCH_HISTORY } from '@/data/assets'
import { CASES_DATA } from '@/data/cases'
import { SHARED_TEMPLATES } from '@/data/shared-templates'
import {
  HOME_STATS, QUICK_START, LATEST_TOPICS, TOP_TEMPLATES,
  RECENT_REPORTS, RECENT_TOPICS, PARTNER_TEMPLATES, PARTNER_TOPICS,
} from '@/data/home-data'
import { getTopicQueries, getAiQueryResult, type AiQueryResult } from '@/data/topics-ai-queries'
import { BUSINESS_TOPICS, type BusinessTopic } from '@/data/topics'
import type { DataAsset } from '@/types'
import type { CaseData } from '@/data/cases'
import type { SharedTemplate } from '@/data/shared-templates'
import { CommentsModal } from '@/components/search/CommentsModal'
import type { UserBizRole } from '@/utils/user-role'
import { ROLE_CATEGORY_MAP } from '@/utils/user-role'
// import { AIChatModal } from '@/components/catalog/AIChatModal'  // 已隐藏
import { cn } from '@/lib/utils'

interface HomePageProps {
  searchMode: boolean
  searchKeyword: string
  onSearch: (keyword: string) => void
  onResetSearch?: () => void
  onLearnMore?: (catalogId: string) => void
  onCaseDetail: (caseId: string) => void
  onTemplateNavigate?: () => void
  onCasesNavigate?: () => void
  onNavigate?: (menu: string) => void
  onViewReport?: (templateId?: string) => void
  onPayrollTopicAnalysis?: () => void
  onSelfAnalysis?: (topicId: string) => void
  version?: string
  currentUser?: string
  userBizRole?: UserBizRole
}

/** 图表可视化组件（首页 AI 问数用） */
function HomeChartVisualization({ result }: { result: AiQueryResult }) {
  const rows = result.rows
  const valueCol = result.columns.find(c =>
    c.key === 'amount' || c.key === 'totalAmount' || c.key === 'balance' || c.key === 'count' || c.key === 'creditAmount' || c.key === 'npl' || c.key === 'dailyAvg' || c.key === 'avgTime' || c.key === 'change' || c.key === 'value'
  )?.key || result.columns[1]?.key
  const labelCol = result.columns[0]?.key
  const values = rows.map(r => {
    const v = r[valueCol]
    return parseFloat(String(v).replace(/[¥,亿元%+\-—,]/g, '')) || 0
  })
  const maxVal = Math.max(...values, 1)
  const chartH = 120
  const points = values.map((v, i) => ({
    x: ((i + 0.5) / rows.length) * 100,
    y: chartH - (v / maxVal) * chartH * 0.85,
  }))
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const barWidth = Math.max(60 / rows.length, 6)

  return (
    <div className="p-4 bg-white">
      <div className="relative h-36">
        <svg viewBox={`0 0 100 ${chartH}`} className="w-full h-full" preserveAspectRatio="none">
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
            <line key={i} x1="0" x2="100" y1={chartH - chartH * pct * 0.85} y2={chartH - chartH * pct * 0.85} stroke="#e5e7eb" strokeWidth="0.2" vectorEffect="non-scaling-stroke" />
          ))}
          {values.map((v, i) => {
            const barH = (v / maxVal) * chartH * 0.85
            const x = ((i + 0.5) / rows.length) * 100 - barWidth / 2
            return (
              <rect key={i} x={x} y={chartH - barH} width={barWidth} height={barH} fill="#c4b5fd" rx="0.5" />
            )
          })}
          <path d={path} fill="none" stroke="#7c3aed" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="1.2" fill="#7c3aed" stroke="#fff" strokeWidth="0.5" />
          ))}
        </svg>
        <div className="absolute bottom-0 inset-x-0 flex">
          {rows.map((r, i) => (
            <div key={i} className="flex-1 flex justify-center">
              <span className="text-[9px] text-gray-400 truncate px-0.5">{String(r[labelCol])}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ======================== HomeSearchBox ======================== */
function HomeSearchBox({ onSearch, version }: { onSearch: (keyword: string) => void; version?: string }) {
  const [keyword, setKeyword] = useState('')

  const handleSearch = () => {
    if (keyword.trim()) {
      onSearch(keyword.trim())
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white px-6 py-8">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-1">智数坊</h1>
        <p className="text-sm text-gray-500 mb-6">{version === '1' ? '搜索报表模板，快速开启数据分析之旅' : '搜索业务主题、共享模板、经典案例，快速开启数据分析之旅'}</p>

        {/* 搜索框 */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="输入关键词搜索，多个关键词用空格分隔..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-11 pl-10 pr-4 text-sm rounded-lg border border-gray-300 focus:border-gcb-primary focus:outline-none focus:ring-2 focus:ring-gcb-primary/20"
            />
          </div>
          <Button
            className="h-11 px-8 bg-gcb-primary hover:bg-gcb-primaryDark text-white text-sm font-medium rounded-lg"
            onClick={handleSearch}
          >
            搜索
          </Button>
        </div>

        {/* 热门搜索 */}
        {SEARCH_HISTORY.length > 0 && (
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400">热门搜索：</span>
            {SEARCH_HISTORY.map((item) => (
              <button
                key={item}
                onClick={() => onSearch(item)}
                className="px-2.5 py-1 rounded-full text-xs bg-white border border-gray-200 text-gray-600 hover:border-gcb-primary hover:text-gcb-primary transition-colors"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ======================== StatsCards ======================== */
function StatsCards({ version }: { version?: string; currentUser?: string }) {
  // 从 localStorage 读取用户共享模板数，与共享模板页面保持一致
  const userSharedCount = useMemo(() => {
    try {
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      return Array.isArray(shared) ? shared.length : 0
    } catch { return 0 }
  }, [])
  const templateCount = SHARED_TEMPLATES.length + userSharedCount

  const allStats = [
    { label: '当前登录用户数', value: HOME_STATS.loginUsers, icon: <Users className="w-5 h-5" />, color: 'from-blue-500 to-blue-600' },
    { label: '业务主题数', value: HOME_STATS.businessTopics, icon: <BookOpen className="w-5 h-5" />, color: 'from-emerald-500 to-emerald-600' },
    { label: '报表模板数', value: templateCount, icon: <LayoutGrid className="w-5 h-5" />, color: 'from-purple-500 to-purple-600' },
    { label: '经典案例数', value: HOME_STATS.classicCases, icon: <Trophy className="w-5 h-5" />, color: 'from-amber-500 to-amber-600' },
  ]
  const stats = version === '1'
    ? allStats.filter(s => s.label === '当前登录用户数' || s.label === '报表模板数')
    : allStats

  return (
    <div className={cn("grid gap-4 mb-6", stats.length === 2 ? "grid-cols-2" : "grid-cols-4")}>
      {stats.map((s) => (
        <Card key={s.label} className="overflow-hidden">
          <CardContent className="p-4 flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-white', s.color)}>
              {s.icon}
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{s.value.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

/* ======================== QuickStartCards ======================== */
function QuickStartCards() {
  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-gcb-primary" />
        <h2 className="text-sm font-semibold text-gray-900">快速入门</h2>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {QUICK_START.map((item) => (
          <a
            key={item.id}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gcb-primary/30 hover:bg-blue-50/50 transition-all group"
          >
            <span className="text-2xl">{item.icon}</span>
            <div>
              <p className="text-sm font-medium text-gray-900 group-hover:text-gcb-primary transition-colors">{item.title}</p>
              <p className="text-xs text-gray-400">点击查看详情 →</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}

/* ======================== LatestTopicsSection ======================== */
function LatestTopicsSection({ onLearnMore, onNavigate, onPayrollTopicAnalysis, onAiQuery }: { onLearnMore?: (catalogId: string) => void; onNavigate?: (menu: string) => void; onPayrollTopicAnalysis?: () => void; onAiQuery?: (topicId: string) => void }) {
  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-gcb-primary" />
          <h2 className="text-sm font-semibold text-gray-900">最新上线</h2>
        </div>
        <span className="text-xs text-gray-400">最新上线的业务主题</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {LATEST_TOPICS.slice(0, 4).map((topic) => (
          <div
            key={topic.id}
            className="rounded-lg border border-gray-100 hover:border-gcb-primary/30 hover:shadow-sm transition-all group flex flex-col relative"
          >
            <button
              className="absolute top-2 right-2 z-10 flex items-center gap-0.5 text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gcb-primary transition-all"
              onClick={(e) => {
                e.stopPropagation()
                topic.catalogId && onLearnMore?.(topic.catalogId)
              }}
            >
              了解更多 <ArrowRight className="w-2.5 h-2.5" />
            </button>
            <div className="p-3 cursor-pointer flex-1" onClick={() => topic.catalogId && onLearnMore?.(topic.catalogId)}>
              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mb-2">
                <BarChart3 className="w-4 h-4 text-gcb-primary" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{topic.name}</h3>
              <p className="text-xs text-gray-400 mt-1 line-clamp-2">{topic.description}</p>
              <p className="text-[10px] text-gray-300 mt-2">{topic.department}</p>
            </div>
            <div className="px-3 pb-3 flex gap-1.5">
              <Button
                size="sm"
                className="flex-1 h-7 text-xs bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-md"
                onClick={() => {
                  const mapping: Record<string, string> = {
                    'topic-payroll': 'topic-payroll',
                    'topic-corp-deposit': 'topic-corp-deposit',
                  }
                  const topicId = mapping[topic.id] || topic.id
                  onAiQuery?.(topicId)
                }}
              >
                <Sparkles className="w-3 h-3 mr-1" /> AI问数
              </Button>
              <Button
                size="sm"
                className="flex-1 h-7 text-xs bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-md"
                onClick={() => {
                  if (topic.id === 'topic-payroll') {
                    onPayrollTopicAnalysis?.()
                  } else {
                    onNavigate?.('report')
                  }
                }}
              >
                <Zap className="w-3 h-3 mr-1" /> 自主分析
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ======================== TopTemplatesTable ======================== */
function TopTemplatesTable({ onMore, onViewReport, version, userBizRole }: { onMore?: () => void; onViewReport?: (templateId?: string) => void; version?: string; userBizRole?: UserBizRole }) {
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('template_favorites')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    localStorage.setItem('template_favorites', JSON.stringify([...favorites]))
  }, [favorites])
  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allowedCategory = userBizRole ? ROLE_CATEGORY_MAP[userBizRole] : null

  const displayTemplates = useMemo(() => {
    // 严格按 TOP_TEMPLATES 原始 rank 顺序展示，仅展示 TOP10
    return TOP_TEMPLATES.filter(t => t.rank <= 10)
  }, [userBizRole])

  const handleViewTemplate = (t: (typeof TOP_TEMPLATES)[0]) => {
    const tpl = SHARED_TEMPLATES.find(s => s.id === t.templateId)
    const isAllowed = !allowedCategory || !tpl || tpl.category === allowedCategory
    if (!isAllowed) {
      // 非匹配模板：提交权限申请（模板级别）
      try {
        const requests = JSON.parse(localStorage.getItem('permission_requests') || '[]')
        requests.push({
          id: `perm-${Date.now()}`,
          templateId: t.templateId,
          templateName: t.name,
          requiredCategory: tpl?.category || '',
          type: 'template',
          status: 'pending',
          requestedAt: new Date().toISOString(),
        })
        localStorage.setItem('permission_requests', JSON.stringify(requests))
      } catch { /* ignore */ }
      alert(`「${t.name}」属于${tpl?.category}，需要该角色权限才能查看。\n权限申请已提交给管理员，预计 1-2 个工作日内处理。`)
      return
    }
    onViewReport?.(t.templateId)
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden mb-6">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-gray-900">热门模板 TOP10</h2>
        </div>
        <button onClick={onMore} className="text-xs text-gcb-primary hover:text-gcb-primaryDark flex items-center gap-0.5">
          更多 <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="px-4 py-2 bg-gray-50 grid grid-cols-12 text-xs font-medium text-gray-500">
        <div className="col-span-1 text-center">排名</div>
        <div className="col-span-3">模板名称</div>
        <div className="col-span-7">模板介绍</div>
        <div className="col-span-1 text-center">查看 / 收藏</div>
      </div>
      {displayTemplates.map((t) => {
        return (
        <div key={t.rank} className="px-4 py-2.5 grid grid-cols-12 items-center text-sm border-t border-gray-50 hover:bg-blue-50/30 transition-colors group">
          <div className="col-span-1 text-center">
            <span className={cn(
              'inline-flex items-center justify-center w-5 h-5 rounded text-xs font-bold',
              t.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
            )}>{t.rank}</span>
          </div>
          <div className="col-span-3 font-medium text-gray-900 truncate pr-2">{t.name}</div>
          <div className="col-span-7 text-gray-500 text-xs pr-2" title={t.introduction}>
            <span className="truncate block">{t.introduction}</span>
          </div>
          <div className="col-span-1 flex items-center justify-center gap-3 text-xs text-gray-400">
            <button
              className="flex items-center gap-1 hover:text-gcb-primary transition-colors cursor-pointer"
              onClick={() => handleViewTemplate(t)}
              title={(() => {
                const tpl = SHARED_TEMPLATES.find(s => s.id === t.templateId)
                return !allowedCategory || !tpl || tpl.category === allowedCategory ? '进入报表制作' : '申请权限'
              })()}
            >
              {(() => {
                const tpl = SHARED_TEMPLATES.find(s => s.id === t.templateId)
                const isAllowed = !allowedCategory || !tpl || tpl.category === allowedCategory
                if (isAllowed) {
                  return <Eye className="w-3 h-3" />
                }
                return <Shield className="w-3 h-3 text-amber-500" />
              })()}
            </button>
            <button
              className="flex items-center gap-1 transition-colors"
              onClick={() => toggleFavorite(t.templateId)}
              title={favorites.has(t.templateId) ? '取消收藏' : '收藏'}
            >
              <Star className={cn("w-3 h-3", favorites.has(t.templateId) ? "fill-yellow-400 text-yellow-400" : "hover:text-yellow-500")} />
            </button>
          </div>
        </div>
        )
      })}
    </div>
  )
}

/* ======================== ClassicCasesSection ======================== */
function ClassicCasesSection({ onCaseDetail, onMore }: { onCaseDetail: (caseId: string) => void; onMore?: () => void }) {
  const displayCases = CASES_DATA.slice(0, 3)
  const gradients = ['from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600']
  const icons = ['📊', '', '🛡️']

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-gcb-primary" />
          <h2 className="text-sm font-semibold text-gray-900">经典案例</h2>
        </div>
        <button onClick={onMore} className="text-xs text-gcb-primary hover:text-gcb-primaryDark flex items-center gap-0.5">
          更多 <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {displayCases.map((c, idx) => (
          <div
            key={c.id}
            className="bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => onCaseDetail(c.id)}
          >
            <div className={cn('h-24 bg-gradient-to-br flex items-center justify-center relative', gradients[idx])}>
              <span className="text-4xl">{icons[idx]}</span>
              <div className="absolute bottom-1.5 right-1.5 bg-black/40 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <Eye className="w-2.5 h-2.5" />{c.viewCount}
              </div>
            </div>
            <div className="p-3">
              <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{c.name}</h3>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{c.intro}</p>
              <div className="mt-2 bg-green-50/60 rounded p-1.5">
                <p className="text-[11px] text-green-700 line-clamp-1">{c.businessResults}</p>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{c.viewCount}</span>
                  <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" />{c.likeCount}</span>
                </div>
                <span className="text-[10px] text-gcb-primary flex items-center gap-0.5">详情 <ArrowRight className="w-2.5 h-2.5" /></span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ======================== RecommendationsSection ======================== */
function RecommendationsSection({ onViewReport, onLearnMore, onPayrollTopicAnalysis }: { onViewReport?: (templateId?: string) => void; onLearnMore?: (catalogId: string) => void; onPayrollTopicAnalysis?: () => void }) {
  const handleReportClick = (id: string) => {
    if (id === 'rpt-1' || id === 'pt-1') {
      // 代发客群报表
      onViewReport?.('11')
    } else if (id === 'rpt-2' || id === 'pt-2') {
      // 对公大额存款报表
      onViewReport?.('1')
    }
  }

  const handleTopicClick = (id: string) => {
    if (id === 'topic-payroll') {
      onPayrollTopicAnalysis?.()
    } else if (id === 'topic-corp-deposit') {
      onLearnMore?.('retail-deposit')
    }
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden mb-6">
      <Tabs defaultValue="recent">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Star className="w-4 h-4 text-gcb-primary" />
          <h2 className="text-sm font-semibold text-gray-900">为我推荐</h2>
        </div>
        <div className="px-4 pt-3">
          <TabsList className="h-8 bg-gray-100">
            <TabsTrigger value="recent" className="text-xs h-7 px-3 data-[state=active]:bg-white data-[state=active]:text-gcb-primary">
              <Clock className="w-3 h-3 mr-1" />最近访问
            </TabsTrigger>
            <TabsTrigger value="partner" className="text-xs h-7 px-3 data-[state=active]:bg-white data-[state=active]:text-gcb-primary">
              <Users className="w-3 h-3 mr-1" />伙伴推荐
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="recent" className="p-4 mt-0">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <BarChart3 className="w-3 h-3" /> 最近访问报表（TOP 5）
              </h3>
              <div className="space-y-1.5">
                {RECENT_REPORTS.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={() => handleReportClick(r.id)}>
                    <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center shrink-0">
                      <BarChart3 className="w-3 h-3 text-gcb-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{r.name}</p>
                      <p className="text-[10px] text-gray-400">{r.department}</p>
                    </div>
                    <span className="text-[10px] text-gray-300 shrink-0">{r.lastAccessed}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> 最近访问主题（TOP 5）
              </h3>
              <div className="space-y-1.5">
                {RECENT_TOPICS.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={() => handleTopicClick(t.id)}>
                    <div className="w-6 h-6 rounded bg-emerald-50 flex items-center justify-center shrink-0">
                      <BookOpen className="w-3 h-3 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{t.name}</p>
                      <p className="text-[10px] text-gray-400">{t.department}</p>
                    </div>
                    <span className="text-[10px] text-gray-300 shrink-0">{t.lastAccessed}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="partner" className="p-4 mt-0">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <LayoutGrid className="w-3 h-3" /> 同岗位热门模板（TOP 5）
              </h3>
              <div className="space-y-1.5">
                {PARTNER_TEMPLATES.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={() => handleReportClick(t.id)}>
                    <div className="w-6 h-6 rounded bg-purple-50 flex items-center justify-center shrink-0">
                      <LayoutGrid className="w-3 h-3 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{t.name}</p>
                      <p className="text-[10px] text-gray-400">{t.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> 同岗位热门主题（TOP 5）
              </h3>
              <div className="space-y-1.5">
                {PARTNER_TOPICS.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={() => handleTopicClick(t.id)}>
                    <div className="w-6 h-6 rounded bg-emerald-50 flex items-center justify-center shrink-0">
                      <BookOpen className="w-3 h-3 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{t.name}</p>
                      <p className="text-[10px] text-gray-400">{t.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

/* ======================== Search Topic Card ======================== */
function SearchTopicCard({ topic, onLearnMore, onSelfAnalysis, onOpenAi }: {
  topic: BusinessTopic
  onLearnMore?: (catalogId: string) => void
  onSelfAnalysis?: (topicId: string) => void
  onOpenAi?: (topicId: string) => void
}) {
  return (
    <div className="p-4 hover:bg-blue-50/30 transition-colors group">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shrink-0">
          <Database className="w-5 h-5 text-gcb-primary" />
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onLearnMore?.(topic.catalogId)}>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-sm text-gray-900 group-hover:text-gcb-primary transition-colors">
              {topic.name}
            </h3>
            <span className="text-[10px] text-gray-400">{topic.department}</span>
          </div>
          <p className="text-xs text-gray-500 mb-2">{topic.description}</p>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <TableIcon className="w-3 h-3" />
              {topic.tableCount} 张表
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {topic.recordCount}
            </span>
            <span className="flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              {topic.updateFrequency}
            </span>
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end justify-between self-stretch">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onLearnMore?.(topic.catalogId)
            }}
            disabled={!topic.catalogId}
            className="flex items-center gap-1 text-xs text-gcb-primary hover:text-gcb-primaryDark transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            了解更多 <ArrowRight className="w-3 h-3" />
          </button>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              className="h-7 text-xs bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-sm"
              onClick={(e) => {
                e.stopPropagation()
                onOpenAi?.(topic.id)
              }}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> AI问数
            </Button>
            {onSelfAnalysis && (
              <Button
                size="sm"
                className="h-7 text-xs bg-gcb-primary hover:bg-gcb-primaryDark text-white"
                onClick={(e) => {
                  e.stopPropagation()
                  onSelfAnalysis(topic.id)
                }}
              >
                <Zap className="w-3 h-3 mr-1" /> 自主分析
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ======================== Search Case Card (matches CasesPage) ======================== */
const COVER_GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-purple-500 to-pink-600',
  'from-red-500 to-rose-600',
  'from-cyan-500 to-blue-600',
]
const COVER_ICONS = ['📊', '📈', '🛡️', '', '🔍', '📋']

function SearchCaseCard({ caseData, idx, onClick, isFavorited, onToggleFavorite }: {
  caseData: CaseData
  idx: number
  onClick: () => void
  isFavorited: boolean
  onToggleFavorite: () => void
}) {
  const gradient = COVER_GRADIENTS[idx % COVER_GRADIENTS.length]
  const icon = COVER_ICONS[idx % COVER_ICONS.length]
  return (
    <div className="bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow group cursor-pointer" onClick={onClick}>
      {/* 封面 */}
      <div className={cn('h-32 bg-gradient-to-br flex items-center justify-center relative', gradient)}>
        <span className="text-5xl">{icon}</span>
        <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {caseData.viewCount}
        </div>
      </div>
      {/* 内容 */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-gcb-primary transition-colors">
          {caseData.name}
        </h3>
        <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">{caseData.intro}</p>
        {/* 业务成效 */}
        <div className="bg-green-50/60 border border-green-100 rounded-md p-2 mb-3">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-green-600" />
            <span className="text-[10px] font-medium text-green-700">业务成效</span>
          </div>
          <p className="text-[11px] text-green-800 line-clamp-2">{caseData.businessResults}</p>
        </div>
        {/* 标签 */}
        <div className="flex flex-wrap gap-1 mb-3">
          <Badge variant="outline" className="text-[10px] h-4 px-1">{caseData.businessLine}</Badge>
          <Badge className="text-[10px] h-4 px-1 bg-green-50 text-green-700 border-green-200">{caseData.scenario}</Badge>
        </div>
        {/* 底部信息 */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3 text-[11px] text-gray-400">
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{caseData.viewCount}</span>
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{caseData.likeCount}</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite() }}
            className={`transition-colors ${
              isFavorited
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-300 hover:text-yellow-400'
            }`}
            title={isFavorited ? '取消收藏' : '收藏'}
          >
            <Heart className={`w-4 h-4 ${isFavorited ? 'fill-yellow-500' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  )
}

/* ======================== Search Template Row (matches SharedTemplates) ======================== */
function SearchTemplateRow({ template, isFavorited, onToggleFavorite, onViewReport, allowedCategory, onDescMouseEnter, onDescMouseLeave }: {
  template: SharedTemplate
  isFavorited: boolean
  onToggleFavorite: () => void
  onViewReport?: () => void
  allowedCategory?: string | null
  onDescMouseEnter?: (text: string, el: HTMLElement) => void
  onDescMouseLeave?: () => void
}) {
  const isAllowed = !allowedCategory || template.category === allowedCategory
  return (
    <div className="grid grid-cols-12 items-center px-4 py-3 border-b border-gray-50 hover:bg-blue-50/30 transition-colors group text-sm last:border-b-0">
      {/* 名称 */}
      <div className="col-span-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shrink-0">
          <ExternalLink className="w-3.5 h-3.5 text-gcb-primary" />
        </div>
        <div>
          <span className="font-medium text-gray-900">{template.name}</span>
          <div className="flex gap-1 mt-0.5">
            <Badge variant="outline" className="text-[10px] h-4 px-1">{template.category}</Badge>
            <Badge className="text-[10px] h-4 px-1 bg-green-50 text-green-700 border-green-200">{template.scenario}</Badge>
          </div>
        </div>
      </div>
      {/* 部门 */}
      <div className="col-span-3 min-w-0 text-gray-600 text-xs truncate pr-2">{template.department}</div>
      {/* 介绍 */}
      <div
        className="col-span-2 min-w-0 pr-2"
        onMouseEnter={e => onDescMouseEnter?.(template.description, e.currentTarget)}
        onMouseLeave={() => onDescMouseLeave?.()}
      >
        <span className="text-gray-500 text-xs truncate block">{template.description}</span>
      </div>
      {/* 时间 */}
      <div className="col-span-1 text-center text-gray-400 text-xs whitespace-nowrap flex items-center justify-center gap-1">
        <Calendar className="w-3 h-3" />
        {template.createdAt}
      </div>
      {/* 点击量 */}
      <div className="col-span-1 text-center text-gray-500 text-xs flex items-center justify-center gap-1">
        <Eye className="w-3 h-3" />
        {template.views}
      </div>
      {/* 收藏量 */}
      <div className="col-span-1 text-center text-gray-500 text-xs flex items-center justify-center gap-1">
        <Star className={`w-3 h-3 ${isFavorited ? 'fill-yellow-500 text-yellow-500' : ''}`} />
        {template.favorites + (isFavorited ? 1 : 0)}
      </div>
      {/* 操作 */}
      <div className="col-span-1 flex items-center justify-center gap-1">
        {isAllowed ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-gcb-primary hover:text-gcb-primaryDark hover:bg-blue-50"
            onClick={onViewReport}
            title="进入报表制作"
          >
            <Eye className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
            onClick={onViewReport}
            title="申请权限"
          >
            <Shield className="w-4 h-4" />
          </Button>
        )}
        <button
          onClick={onToggleFavorite}
          className={`transition-colors ${
            isFavorited
              ? 'text-yellow-500 hover:text-yellow-600'
              : 'text-gray-300 hover:text-yellow-400'
          }`}
          title={isFavorited ? '取消收藏' : '收藏'}
        >
          <Star className={`w-4 h-4 ${isFavorited ? 'fill-yellow-500' : ''}`} />
        </button>
      </div>
    </div>
  )
}

/* ======================== Search Results View ======================== */
function SearchResultsView({
  keyword,
  topics,
  onLearnMore,
  onCaseDetail,
  onViewReport,
  onSelfAnalysis,
  onOpenAi,
  version,
  userBizRole,
}: {
  keyword: string
  topics: BusinessTopic[]
  onLearnMore?: (catalogId: string) => void
  onCaseDetail: (caseId: string) => void
  onViewReport?: (templateId?: string) => void
  onSelfAnalysis?: (topicId: string) => void
  onOpenAi?: (topicId: string) => void
  version?: string
  userBizRole?: UserBizRole
}) {
  const [activeTab, setActiveTab] = useState<string>('templates')
  const hoveredDescRef = useRef<{ text: string; rect: DOMRect } | null>(null)
  const [_hoveredDescTick, setHoveredDescTick] = useState(0)

  const handleDescMouseEnter = (text: string, el: HTMLElement) => {
    hoveredDescRef.current = { text, rect: el.getBoundingClientRect() }
    setHoveredDescTick(n => n + 1)
  }

  const handleDescMouseLeave = () => {
    hoveredDescRef.current = null
    setHoveredDescTick(n => n + 1)
  }

  const [templateFavorites, setTemplateFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('template_favorites')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    localStorage.setItem('template_favorites', JSON.stringify([...templateFavorites]))
  }, [templateFavorites])
  const [caseFavorites, setCaseFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('case_favorites')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    localStorage.setItem('case_favorites', JSON.stringify([...caseFavorites]))
  }, [caseFavorites])

  const keywords = keyword.split(/\s+/).filter(Boolean).map(k => k.toLowerCase())

  const allowedCategory = userBizRole ? ROLE_CATEGORY_MAP[userBizRole] : null

  const filteredTemplates = SHARED_TEMPLATES.filter(t => {
    if (!keywords.length) return false
    // 不再硬过滤，非匹配模板也展示
    return keywords.every(kw =>
      t.name.toLowerCase().includes(kw) ||
      t.description.toLowerCase().includes(kw) ||
      t.category.toLowerCase().includes(kw) ||
      (t.tags?.some(tag => tag.toLowerCase().includes(kw)) ?? false)
    )
  }).sort((a, b) => {
    // 排序：匹配角色业务的在前，非匹配的排在后方
    const aAllowed = !allowedCategory || a.category === allowedCategory
    const bAllowed = !allowedCategory || b.category === allowedCategory
    if (aAllowed && !bAllowed) return -1
    if (!aAllowed && bAllowed) return 1
    return 0
  })

  const filteredCases = CASES_DATA.filter(c => {
    if (!keywords.length) return false
    return keywords.every(kw =>
      c.name.toLowerCase().includes(kw) ||
      c.intro.toLowerCase().includes(kw) ||
      c.keywords.some(k => k.toLowerCase().includes(kw))
    )
  })

  const toggleTemplateFavorite = (id: string) => {
    setTemplateFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleCaseFavorite = (id: string) => {
    setCaseFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="bg-white border-b px-6">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="h-10 bg-transparent border-b-0 gap-0 p-0">
              <TabsTrigger
                value="templates"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-gcb-primary data-[state=active]:bg-transparent data-[state=active]:text-gcb-primary text-sm px-4 h-10"
              >
                报表模板库 <Badge className="ml-1.5 text-[10px] h-4 px-1.5 bg-gray-100 text-gray-500">{filteredTemplates.length}</Badge>
              </TabsTrigger>
              {version !== '1' && (
              <TabsTrigger
                value="topics"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-gcb-primary data-[state=active]:bg-transparent data-[state=active]:text-gcb-primary text-sm px-4 h-10"
              >
                业务主题 <Badge className="ml-1.5 text-[10px] h-4 px-1.5 bg-gray-100 text-gray-500">{topics.length}</Badge>
              </TabsTrigger>
              )}
              {version !== '1' && (
              <TabsTrigger
                value="cases"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-gcb-primary data-[state=active]:bg-transparent data-[state=active]:text-gcb-primary text-sm px-4 h-10"
              >
                经典案例 <Badge className="ml-1.5 text-[10px] h-4 px-1.5 bg-gray-100 text-gray-500">{filteredCases.length}</Badge>
              </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="templates" className="p-6 mt-0">
              <div className="mb-3">
                <span className="text-xs text-gray-500">找到 <span className="font-medium text-gray-900">{filteredTemplates.length}</span> 个匹配的共享模板</span>
              </div>
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  未找到与"<span className="text-gcb-primary">{keyword}</span>"相关的共享模板
                </div>
              ) : (
                <div className="bg-white rounded-lg border overflow-hidden">
                  {/* 表头 */}
                  <div className="h-10 bg-gray-50 border-b grid grid-cols-12 items-center px-4 text-xs font-medium text-gray-500">
                    <div className="col-span-3 min-w-0">模板名称</div>
                    <div className="col-span-3 min-w-0">制作部门</div>
                    <div className="col-span-2 min-w-0">模板介绍</div>
                    <div className="col-span-1 text-center whitespace-nowrap">创建时间</div>
                    <div className="col-span-1 text-center whitespace-nowrap">点击量</div>
                    <div className="col-span-1 text-center whitespace-nowrap">收藏量</div>
                    <div className="col-span-1 text-center">操作</div>
                  </div>
                  {/* 列表 */}
                  <div className="relative">
                  <ScrollArea className="h-[420px]">
                    {filteredTemplates.map((t) => (
                      <SearchTemplateRow
                        key={t.id}
                        template={t}
                        isFavorited={templateFavorites.has(t.id)}
                        onToggleFavorite={() => toggleTemplateFavorite(t.id)}
                        onViewReport={() => onViewReport?.(t.id)}
                        allowedCategory={allowedCategory}
                        onDescMouseEnter={handleDescMouseEnter}
                        onDescMouseLeave={handleDescMouseLeave}
                      />
                    ))}
                  </ScrollArea>
                  {/* 悬浮描述 tooltip */}
                  {hoveredDescRef.current && (
                    <div
                      className="fixed z-[9999] max-w-[400px] px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg pointer-events-none whitespace-normal"
                      style={{
                        left: `${hoveredDescRef.current.rect.left}px`,
                        top: `${hoveredDescRef.current.rect.top - 8}px`,
                        transform: 'translateY(-100%)',
                      }}
                    >
                      {hoveredDescRef.current.text}
                    </div>
                  )}
                  </div>
                </div>
              )}
            </TabsContent>

            {version !== '1' && (
            <TabsContent value="topics" className="p-6 mt-0">
              <div className="mb-3">
                <span className="text-xs text-gray-500">找到 <span className="font-medium text-gray-900">{topics.length}</span> 个匹配的业务主题</span>
              </div>
              {topics.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  未找到与"<span className="text-gcb-primary">{keyword}</span>"相关的业务主题
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      <div className="divide-y divide-gray-100">
                        {topics.map((topic) => (
                          <SearchTopicCard
                            key={topic.id}
                            topic={topic}
                            onLearnMore={onLearnMore}
                            onSelfAnalysis={onSelfAnalysis}
                            onOpenAi={onOpenAi}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            )}

            {version !== '1' && (
            <TabsContent value="cases" className="p-6 mt-0">
              <div className="mb-3">
                <span className="text-xs text-gray-500">找到 <span className="font-medium text-gray-900">{filteredCases.length}</span> 个匹配的经典案例</span>
              </div>
              {filteredCases.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  未找到与"<span className="text-gcb-primary">{keyword}</span>"相关的经典案例
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCases.map((c, idx) => (
                    <SearchCaseCard
                      key={c.id}
                      caseData={c}
                      idx={idx}
                      onClick={() => onCaseDetail(c.id)}
                      isFavorited={caseFavorites.has(c.id)}
                      onToggleFavorite={() => toggleCaseFavorite(c.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  )
}

/* ======================== HomePage Main ======================== */
export function HomePage({
  searchMode,
  searchKeyword,
  onSearch,
  onResetSearch: _onResetSearch,
  onLearnMore,
  onCaseDetail,
  onTemplateNavigate,
  onCasesNavigate,
  onNavigate,
  onViewReport,
  onPayrollTopicAnalysis,
  onSelfAnalysis,
  version,
  currentUser,
  userBizRole,
}: HomePageProps) {
  const [_showCommentsModal, _setShowCommentsModal] = useState(false)
  const [_selectedAsset, _setSelectedAsset] = useState<DataAsset | null>(null)

  /* ---- AI问数状态 ---- */
  const [showAiDialog, setShowAiDialog] = useState(false)
  const [aiTopicId, setAiTopicId] = useState('')
  const [aiTopicName, setAiTopicName] = useState('')
  const [aiQueryInput, setAiQueryInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiHistory, setAiHistory] = useState<AiQueryResult[]>([])
  const [aiSelectedQueryId, setAiSelectedQueryId] = useState<string | null>(null)
  const openAiDialog = (topicId: string) => {
    const topic = BUSINESS_TOPICS.find(t => t.id === topicId)
    if (!topic) return
    setAiTopicId(topicId)
    setAiTopicName(topic.name)
    setAiQueryInput('')
    setAiHistory([])
    setAiSelectedQueryId(null)
    setShowAiDialog(true)
  }
  const handleSelectQuery = (queryId: string, queryText: string) => {
    setAiSelectedQueryId(queryId)
    setAiQueryInput(queryText)
    runAiQuery(queryId)
  }
  const runAiQuery = (queryId?: string) => {
    if (!queryId) {
      setAiSelectedQueryId(null)
      setAiLoading(true)
      setTimeout(() => {
        const fallback: AiQueryResult = {
          query: aiQueryInput,
          resultType: 'table',
          title: aiQueryInput,
          summary: `关于"${aiQueryInput}"的分析结果如下：`,
          columns: [
            { key: 'dim', label: '维度' },
            { key: 'value', label: '数值' },
          ],
          rows: [
            { dim: '指标A', value: '1,256' },
            { dim: '指标B', value: '8,520' },
            { dim: '指标C', value: '3,680' },
          ],
        }
        setAiHistory(prev => [...prev, fallback])
        setAiLoading(false)
      }, 1200)
      return
    }
    setAiLoading(true)
    setTimeout(() => {
      const result = getAiQueryResult(queryId)
      if (result) setAiHistory(prev => [...prev, result])
      setAiLoading(false)
    }, 1200)
  }
  const handleDownloadResult = (result: AiQueryResult) => {
    const header = result.columns.map(c => c.label).join('\t')
    const rows = result.rows.map(r =>
      result.columns.map(c => r[c.key] ?? '').join('\t')
    ).join('\n')
    const content = `${result.title}\n${result.summary || ''}\n\n${header}\n${rows}`
    const blob = new Blob([content], { type: 'text/tab-separated-values;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.title}.tsv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // 搜索关键词匹配（多关键词）
  const matchKeywords = (item: { name: string; description?: string; keywords?: string[] }, kw: string[]) => {
    return kw.every(k =>
      item.name.toLowerCase().includes(k) ||
      (item.description?.toLowerCase().includes(k) ?? false) ||
      (item.keywords?.some(tk => tk.toLowerCase().includes(k)) ?? false)
    )
  }

  const filteredTopics = BUSINESS_TOPICS.filter(t => matchKeywords(t, searchKeyword.split(/\s+/).filter(Boolean).map(k => k.toLowerCase())))

  const _handleOpenComments = (asset: DataAsset) => {
    _setSelectedAsset(asset)
    _setShowCommentsModal(true)
  }

  if (searchMode) {
    return (
      <div className="flex-1 overflow-auto flex flex-col">
        {/* 搜索结果顶部搜索框 */}
        <div className="bg-white px-6 py-3 border-b">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchKeyword}
                onChange={(e) => onSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch(searchKeyword)}
                className="h-9 pl-10 pr-4 text-sm rounded-md border border-gray-300 focus:border-gcb-primary focus:outline-none focus:ring-1 focus:ring-gcb-primary"
              />
            </div>
            <Button
              size="sm"
              className="h-9 px-6 bg-gcb-primary hover:bg-gcb-primaryDark text-white text-sm rounded-md"
              onClick={() => onSearch(searchKeyword)}
            >
              搜索
            </Button>
          </div>
          {/* 热门搜索 */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-400">热门搜索：</span>
            {SEARCH_HISTORY.map((item) => (
              <button
                key={item}
                onClick={() => onSearch(item)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-xs border transition-colors",
                  searchKeyword === item
                    ? "bg-gcb-primary/10 border-gcb-primary text-gcb-primary"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gcb-primary hover:text-gcb-primary"
                )}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <SearchResultsView
          keyword={searchKeyword}
          topics={filteredTopics}
          onLearnMore={onLearnMore}
          onCaseDetail={onCaseDetail}
          onViewReport={onViewReport}
          onSelfAnalysis={onSelfAnalysis}
          onOpenAi={openAiDialog}
          version={version}
          userBizRole={userBizRole}
        />

        <CommentsModal open={_showCommentsModal} onOpenChange={_setShowCommentsModal} asset={_selectedAsset} />
        {/* AI 问数 - 已隐藏 */}
        {/* <AIChatModal
          open={showAIChatModal}
          onOpenChange={setShowAIChatModal}
          catalogId={_selectedAsset?.catalogId || ''}
          catalogName={_selectedAsset?.name || ''}
          onSaveToSpace={(result, question) => {
            console.log('保存到空间:', { result, question })
            setShowAIChatModal(false)
          }}
        /> */}
      </div>
    )
  }

  // 默认首页
  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <HomeSearchBox onSearch={onSearch} version={version} />
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <StatsCards version={version} />
          <TopTemplatesTable onMore={onTemplateNavigate} onViewReport={onViewReport} version={version} userBizRole={userBizRole} />
          {version !== '1' && (
            <>
              <ClassicCasesSection onCaseDetail={onCaseDetail} onMore={onCasesNavigate} />
              <RecommendationsSection onViewReport={onViewReport} onLearnMore={onLearnMore} onPayrollTopicAnalysis={onPayrollTopicAnalysis} />
            </>
          )}
          <QuickStartCards />
          {version !== '1' && (
            <LatestTopicsSection onLearnMore={onLearnMore} onNavigate={onNavigate} onPayrollTopicAnalysis={onPayrollTopicAnalysis} onAiQuery={openAiDialog} />
          )}
        </div>
      </div>

      {/* AI问数 Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="sm:max-w-[700px] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0">
          <div className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-5 h-5 text-violet-500" />
              AI问数 — {aiTopicName}
            </DialogTitle>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
            {aiHistory.length === 0 && !aiLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3 max-w-sm">
                  <p className="text-sm text-gray-700">您好！我是 AI 数据助手，可以帮您分析 <strong>{aiTopicName}</strong> 相关数据。</p>
                  <p className="text-sm text-gray-700 mt-1">请选择下方推荐问题，或自行输入查询，可连续提问。</p>
                </div>
              </div>
            )}

            {aiHistory.map((item, idx) => (
              <div key={idx} className="space-y-3">
                <div className="flex justify-end">
                  <div className="bg-violet-600 text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-md">
                    <p className="text-sm">{item.query}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 border rounded-lg overflow-hidden bg-white shadow-sm">
                    <div className="bg-violet-50 px-4 py-2.5 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {item.resultType === 'chart' ? <TrendingUp className="w-4 h-4 text-violet-600" /> : <TableIcon className="w-4 h-4 text-violet-600" />}
                          <h4 className="text-sm font-semibold text-gray-800">{item.title}</h4>
                        </div>
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-white text-violet-600 border-violet-200">
                          {item.resultType === 'chart' ? '图表' : '表格'}
                        </Badge>
                      </div>
                      {item.summary && <p className="text-xs text-gray-500 mt-1">{item.summary}</p>}
                    </div>
                    {item.resultType === 'chart' && <HomeChartVisualization result={item} />}
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            {item.columns.map(col => (
                              <th key={col.key} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {item.rows.map((row, ri) => (
                            <tr key={ri} className="border-b last:border-0 hover:bg-gray-50/50">
                              {item.columns.map(col => (
                                <td key={col.key} className="px-3 py-2 text-gray-700 whitespace-nowrap">{row[col.key] as string | number}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-3 py-2 bg-gray-50 border-t flex justify-end">
                      <Button size="sm" variant="outline" onClick={() => handleDownloadResult(item)} className="text-xs h-7">
                        <Download className="w-3.5 h-3.5 mr-1" /> 下载结果
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {aiLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                    <p className="text-sm text-gray-500">AI正在分析您的问题...</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t bg-gray-50/60 px-6 py-3 shrink-0">
            <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> 推荐问数 <span className="text-[10px] text-gray-400 font-normal">（点击可继续提问）</span>
            </h4>
            <div className="space-y-1.5">
              {getTopicQueries(aiTopicId).map(q => (
                <button
                  key={q.id}
                  onClick={() => handleSelectQuery(q.id, q.query)}
                  disabled={aiLoading}
                  className={cn(
                    'w-full text-left p-2.5 rounded-lg border transition-all text-sm disabled:opacity-50',
                    aiSelectedQueryId === q.id
                      ? 'border-violet-300 bg-violet-50 ring-1 ring-violet-200'
                      : 'border-gray-200 hover:border-violet-200 hover:bg-white'
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-gray-800">{q.query}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{q.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t bg-white px-6 py-3 shrink-0">
            <div className="flex items-center gap-2">
              <Input
                placeholder="输入问题，按回车发送..."
                value={aiQueryInput}
                onChange={(e) => setAiQueryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && aiQueryInput.trim() && !aiLoading) runAiQuery() }}
                className="text-sm"
              />
              <Button size="sm" disabled={!aiQueryInput.trim() || aiLoading} onClick={() => runAiQuery()} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0 h-9">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="border-t px-6 py-2 flex justify-end shrink-0">
            <Button variant="outline" size="sm" onClick={() => setShowAiDialog(false)}>关闭</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
